import { NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { Amenity, AmenityType, IAmenity } from '@curbnturf/entities';
import {
  amenityFormBuilder,
  DisplayValidationErrorsContainerDirective,
  DisplayValidationErrorsDirective,
  EnumSelectorPipe,
  extraFormBuilder,
  IAmenityForm,
  IListingExtraForm,
  ISignalReceptionForm,
  signalReceptionFormBuilder,
} from '@curbnturf/form-helpers';
import { IonicModule } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { GeneralAmenityValidator } from '../validators';

@Component({
  selector: 'curbnturf-listing-amenities',
  templateUrl: './listing-amenities.component.html',
  styleUrls: ['./listing-amenities.component.scss'],
  standalone: true,
  imports: [
    DisplayValidationErrorsContainerDirective,
    DisplayValidationErrorsDirective,
    FormsModule,
    IonicModule,
    NgFor,
    NgIf,
    NgClass,
    NgSelectModule,
    ReactiveFormsModule,
    SiteSvgsComponent,
  ],
})
export class ListingAmenitiesComponent implements AfterViewInit {
  electricityError?: string;
  potableWaterError?: string;
  wifiError?: string;

  get electricityOption() {
    return (this.amenities.controls.find(this.match('electricity')) as FormGroup<IAmenityForm>)?.get(
      'options',
    ) as FormControl;
  }

  get customAmenities() {
    if (this.amenities) {
      return this.amenities ? this.amenities.controls.filter((amenity) => amenity.value.type === 'custom') : [];
    }

    return;
  }

  get customTypes() {
    if (this.customAmenities) {
      return this.customAmenities.map((amenity) => amenity.value.custom);
    }

    return;
  }

  get extraTent(): FormGroup | undefined {
    if (this.extras) {
      let extraTent = this.extras.controls.find(
        (extra) => extra.value.label === 'extraTent',
      ) as FormGroup<IListingExtraForm>;

      if (!extraTent) {
        extraTent = extraFormBuilder(this.fb, { label: 'extraTent' });
        this.extras.push(extraTent);
      }

      return extraTent;
    }

    return;
  }

  get extraParking(): FormGroup | undefined {
    if (this.extras) {
      let extraVehicle = this.extras.controls.find(
        (extra) => extra.value.label === 'extraParking',
      ) as FormGroup<IListingExtraForm>;

      if (!extraVehicle) {
        extraVehicle = extraFormBuilder(this.fb, { label: 'extraParking' });
        this.extras.push(extraVehicle);
      }

      return extraVehicle;
    }

    return;
  }
  @Input() amenities: FormArray<FormGroup<IAmenityForm>>;
  @Input() signals: FormArray<FormGroup<ISignalReceptionForm>>;
  @Input() extras: FormArray<FormGroup<IListingExtraForm>>;
  @Input() showCustom: boolean;
  @Input() boondock: boolean = false;

  amenityEnum = new EnumSelectorPipe().transform(AmenityType).reduce(
    (amenityTypes, amenityType) => {
      if (
        amenityType.key !== 'pullThrough' &&
        amenityType.key !== 'backIn' &&
        amenityType.key !== 'wheelchair' &&
        amenityType.key !== 'custom'
      ) {
        amenityTypes.push(amenityType);
      }

      return amenityTypes;
    },
    [] as { key: string; value: string }[],
  );

  wifiUpload: FormControl<string | null>;
  wifiDownload: FormControl<string | null>;

  expanded: (boolean | undefined)[] = [];

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
  ) {
    this.wifiUpload = this.fb.control('');
    this.wifiDownload = this.fb.control('');
  }

  ngOnInit() {
    if (this.amenities) {
      this.amenityUpdate();
      this.amenities.setValidators(GeneralAmenityValidator);
    }
  }

  ngAfterViewInit() {
    if (this.amenities) {
      this.amenities.valueChanges.subscribe(() => {
        this.amenityUpdate();
      });
    }
  }

  amenityUpdate() {
    if (!this.amenities || !this.amenityEnum) {
      return;
    }

    this.expanded = this.amenityEnum.map((amenityType) => {
      const amenityForm = this.amenities.controls.find(
        this.match(amenityType.key as IAmenity['type']),
      ) as FormGroup<IAmenityForm>;
      if (amenityForm) {
        return !!amenityForm.value.description;
      }

      return false;
    });

    if (this.customAmenities) {
      this.customAmenities.forEach((amenity) => this.expanded.push(!!amenity.value.description));
    }

    const wifi = this.amenities.value.find((amenity: Amenity) => amenity.type === 'wifi');
    if (wifi && wifi.options) {
      const downUp = wifi.options.split('/');
      this.wifiDownload.setValue(downUp[0] || '');
      this.wifiUpload.setValue(downUp[1] || '');
    }
  }

  selected(amenity: IAmenity['type'] | string): boolean {
    if (this.amenities) {
      return !!this.amenities.controls.find(this.match(amenity));
    }

    return false;
  }

  selectedCustom(amenity: string): boolean {
    if (this.amenities) {
      return !!this.amenities.controls.find(this.matchCustom(amenity));
    }

    return false;
  }

  toggle(amenity: IAmenity['type'] | string) {
    if (this.selected(amenity)) {
      if (amenity === 'tents' && this.extraTent) {
        const countForm = this.extraTent.get('count') as FormControl<number | null>;
        if (countForm && countForm.value) {
          countForm.setValue(0);
        }
      }

      if (amenity === 'vehicles' && this.extraParking) {
        const countForm = this.extraParking.get('count') as FormControl<number | null>;
        if (countForm && countForm.value) {
          countForm.setValue(0);
        }
      }

      this.amenities.removeAt(this.amenities.controls.findIndex(this.match(amenity)));
    } else {
      this.amenities.push(amenityFormBuilder(this.fb, { type: amenity as IAmenity['type'] }));

      if (amenity === 'cell' && this.signals.controls.length === 0) {
        this.signals.push(signalReceptionFormBuilder(this.fb));
      }

      if (amenity === 'tents' && this.extraTent) {
        const countForm = this.extraTent.get('count') as FormControl<number | null>;
        if (countForm && !countForm.value) {
          countForm.setValue(1);
        }
      }

      if (amenity === 'vehicles' && this.extraParking) {
        const countForm = this.extraParking.get('count') as FormControl<number | null>;
        if (countForm && !countForm.value) {
          countForm.setValue(1);
        }
      }
    }
    this.amenities.markAsDirty();
  }

  toggleCustom(amenity: string) {
    if (this.selectedCustom(amenity)) {
      this.amenities.removeAt(this.amenities.controls.findIndex(this.matchCustom(amenity)));
    } else {
      this.enableCustom(amenity);
    }

    this.amenities.markAsDirty();
  }

  addCustom(event: any) {
    this.enableCustom(event.target.value);
    event.target.value = '';
  }

  enableCustom(customType: string) {
    this.amenities.push(amenityFormBuilder(this.fb, { type: 'custom', custom: customType }));

    this.amenities.markAsDirty();
  }

  expand(index: number) {
    this.expanded[index] = true;
  }

  description(amenity: IAmenity['type'] | string) {
    const amenityForm = this.amenities.controls.find(this.match(amenity)) as FormGroup<IAmenityForm>;

    if (amenityForm) {
      return amenityForm.get('description');
    }

    return;
  }

  descriptionCustom(amenity: string) {
    const amenityForm = this.amenities.controls.find(this.matchCustom(amenity)) as FormGroup<IAmenityForm>;

    if (amenityForm) {
      return amenityForm.get('description');
    }

    return;
  }

  options(amenity: IAmenity['type'] | string) {
    const amenityForm = this.amenities.controls.find(this.match(amenity)) as FormGroup<IAmenityForm>;

    if (amenityForm) {
      return amenityForm.get('options');
    }

    return;
  }

  wifiUpdate() {
    const wifiForm = this.amenities.controls.find(this.match('wifi')) as FormGroup<IAmenityForm>;

    if (wifiForm) {
      const optionsInput = wifiForm.get('options');
      if (optionsInput) {
        optionsInput.setValue(`${this.wifiDownload.value}/${this.wifiUpload.value}`);
        this.amenities.markAsDirty();
      }
    }
  }

  selectElectricity(value: string, event: Event) {
    event.preventDefault();
    const control = this.amenities.controls.find(this.match('electricity')) as FormGroup<IAmenityForm>;

    if (control) {
      control.patchValue({ options: value });
      control.markAsTouched();
      control.markAsDirty();
      setTimeout(() => {
        this.cd.detectChanges();
      });
    }
  }

  disableEvent($event: any) {
    $event.preventDefault();
  }

  public validate() {
    const electricControl = this.amenities.controls.find(this.match('electricity')) as FormGroup;
    if (electricControl && electricControl?.value?.options === '') {
      this.electricityError = 'Must select electric connection type.';
    } else {
      this.electricityError = undefined;
    }
    const waterControl = this.amenities.controls.find(this.match('water')) as FormGroup;
    if (waterControl && waterControl?.value?.options === '') {
      this.potableWaterError = 'Is there a direct hookup?';
    } else {
      this.potableWaterError = undefined;
    }
    const wifiControl = this.amenities.controls.find(this.match('wifi')) as FormGroup;
    const wifiControlValue = wifiControl?.value?.options;
    if (wifiControl && wifiControl.value?.options === '') {
      this.wifiError = 'Enter WiFi speeds.';
    } else if (wifiControl) {
      if (wifiControlValue.indexOf('/') !== -1) {
        const wifiSplit = wifiControlValue.split('/');
        if (!/^\d+$/.test(wifiSplit[0]) || !/^\d+$/.test(wifiSplit[1])) {
          this.wifiError = 'Include both upload and download speed.';
        } else {
          this.wifiError = undefined;
        }
      } else {
        this.wifiError = undefined;
      }
    } else {
      this.wifiError = undefined;
    }
  }

  private match(amenity: IAmenity['type'] | string): (form: UntypedFormGroup) => boolean {
    return (amenityValue) => amenityValue.value.type === amenity;
  }

  private matchCustom(amenity: string): (form: FormGroup<IAmenityForm>) => boolean {
    return (amenityValue) => amenityValue.value.type === 'custom' && amenityValue.value.custom === amenity;
  }
}
